import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlay, FiStopCircle, FiClock, FiUsers, FiBook, FiLink, FiLogOut, FiVideo } from 'react-icons/fi';
import { teacherGetMyClasses, teacherStartClass, teacherEndClass, teacherAddReplay } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const statusColor = {
  scheduled: { bg: '#f0f4ff', color: '#2563eb', label: '📅 Scheduled' },
  live:      { bg: '#f0fdf4', color: '#16a34a', label: '🔴 LIVE NOW' },
  ended:     { bg: '#f8fafc', color: '#64748b', label: '✅ Ended' },
  cancelled: { bg: '#fef2f2', color: '#dc2626', label: '❌ Cancelled' },
};

export default function TeacherDashboard() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [classes,      setClasses]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [replayModal,  setReplayModal]  = useState(null); // classId
  const [replayUrl,    setReplayUrl]    = useState('');
  const [savingReplay, setSavingReplay] = useState(false);
  const [activeClass,  setActiveClass]  = useState(null); // class being taught live

  const load = () => {
    setLoading(true);
    teacherGetMyClasses()
      .then(r => setClasses(r.data.classes || []))
      .catch(() => toast.error('Failed to load classes'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const startClass = async (cls) => {
  try {
    await teacherStartClass(cls._id);
    toast.success('Class is now LIVE! 🔴');
    load();
    // Open Google Meet in new tab
    if (cls.meetLink) {
      window.open(cls.meetLink, '_blank');
    }
  } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
};
  const endClass = async (cls) => {
    if (!window.confirm('End this live class?')) return;
    try {
      await teacherEndClass(cls._id);
      toast.success('Class ended. Add replay link when ready.');
      setActiveClass(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const saveReplay = async () => {
    if (!replayUrl.trim()) { toast.error('Enter replay URL'); return; }
    setSavingReplay(true);
    try {
      await teacherAddReplay(replayModal, { replayUrl });
      toast.success('Replay link saved! Students can access it in 1 hour ✅');
      setReplayModal(null); setReplayUrl(''); load();
    } catch { toast.error('Failed to save replay'); }
    finally { setSavingReplay(false); }
  };

  const upcoming = classes.filter(c => c.status === 'scheduled');
  const live     = classes.filter(c => c.status === 'live');
  const past     = classes.filter(c => c.status === 'ended' || c.status === 'cancelled');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <style>{`
        .td-header { background: linear-gradient(135deg, #0f172a, #1a3a5c); padding: 20px 28px; display: flex; align-items: center; justify-content: space-between; }
        .td-body { max-width: 960px; margin: 0 auto; padding: 28px 24px; }
        .td-section-title { font-size: 16px; font-weight: 700; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
        .cls-card { background: white; border: 1px solid var(--border); border-radius: 14px; padding: 20px 22px; margin-bottom: 12px; transition: var(--transition); }
        .cls-card:hover { box-shadow: var(--shadow-md); }
        .cls-card.live-card { border-color: #16a34a; border-width: 2px; background: linear-gradient(to right, #f0fdf4, white); }
        .cls-meta { display: flex; gap: 20px; flex-wrap: wrap; margin-top: 10px; }
        .cls-meta-item { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--mid); }
        .cls-actions { display: flex; gap: 10px; margin-top: 16px; flex-wrap: wrap; }
        .live-badge { display: inline-flex; align-items: center; gap: 6px; background: #16a34a; color: white; padding: '6px 14px'; border-radius: 999px; font-size: 13px; font-weight: 700; animation: pulse 1.5s infinite; }
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.7 } }
        .jitsi-wrap { position: fixed; inset: 0; background: #000; z-index: 900; display: flex; flex-direction: column; }
        .jitsi-bar { background: #1e293b; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
      `}</style>

      {/* Jitsi live class overlay */}
      {/* Daily.co live class overlay */}
{activeClass && (
  <div className="jitsi-wrap">
    <div className="jitsi-bar">
      <div style={{ color: 'white', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'pulse 1s infinite' }} />
        LIVE: {activeClass.title}
      </div>
      <button onClick={() => endClass(activeClass)}
        className="btn btn-sm" style={{ background: '#ef4444', color: 'white', fontWeight: 700 }}>
        <FiStopCircle size={14} /> End Class
      </button>
    </div>

    {activeClass.meetLink ? (
      <iframe
        src={activeClass.meetLink}
        style={{ flex: 1, border: 'none' }}
        allow="camera; microphone; fullscreen; display-capture"
        title="Live Class"
      />
    ) : (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, background: '#0f172a' }}>
        <div style={{ color: 'white', fontSize: 16 }}>Setting up video room...</div>
        <div style={{ color: '#64748b', fontSize: 13 }}>If this takes too long, check DAILY_API_KEY in .env</div>
      </div>
    )}
  </div>
)}

      {/* Header */}
      <div className="td-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, var(--primary), var(--accent))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 20 }}>
            {user?.fullName?.[0] || 'T'}
          </div>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: 18 }}>Welcome, {user?.fullName?.split(' ')[0]}!</div>
            <div style={{ color: '#94a3b8', fontSize: 13 }}>Teacher Dashboard · Korpnex</div>
          </div>
        </div>
        <button onClick={handleLogout} className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
          <FiLogOut size={14} /> Logout
        </button>
      </div>

      {/* Replay modal */}
      {replayModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 500, width: '100%' }}>
            <h3 style={{ marginBottom: 8 }}>📹 Add Replay Link</h3>
            <p style={{ color: 'var(--mid)', fontSize: 14, marginBottom: 20 }}>
              Upload the class recording to YouTube (unlisted) or Google Drive, then paste the link here. Students will see it after 1 hour.
            </p>
            <div className="form-group">
              <label>Recording URL</label>
              <input value={replayUrl} onChange={e => setReplayUrl(e.target.value)}
                className="form-control" placeholder="https://youtube.com/watch?v=... or Google Drive link" autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setReplayModal(null); setReplayUrl(''); }} className="btn btn-outline">Cancel</button>
              <button onClick={saveReplay} disabled={savingReplay} className="btn btn-primary">
                {savingReplay ? 'Saving...' : '✓ Save Replay Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="td-body">
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { icon: '🔴', label: 'Live Now',  value: live.length },
            { icon: '📅', label: 'Upcoming',  value: upcoming.length },
            { icon: '✅', label: 'Completed', value: past.length },
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-display)' }}>{s.value}</div>
              <div style={{ fontSize: 13, color: 'var(--mid)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : (
          <>
            {/* Live classes */}
            {live.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <div className="td-section-title">
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'pulse 1s infinite' }} />
                  Currently Live
                </div>
                {live.map(cls => (
                  <div key={cls._id} className="cls-card live-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>{cls.title}</div>
                        <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>🔴 Class is LIVE</div>
                      </div>
                      <span style={{ background: '#16a34a', color: 'white', padding: '4px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>LIVE</span>
                    </div>
                    <div className="cls-meta">
                      <span className="cls-meta-item"><FiBook size={13} /> {cls.subject}</span>
                      <span className="cls-meta-item"><FiClock size={13} /> {cls.duration} min</span>
                      {cls.course && <span className="cls-meta-item"><FiUsers size={13} /> {cls.course.title}</span>}
                    </div>
                    <div className="cls-actions">
                     <button onClick={() => window.open(cls.meetLink, '_blank')}
  className="btn btn-primary">
  <FiVideo size={14} /> Rejoin Class Room
</button>
                      <button onClick={() => endClass(cls)} className="btn btn-sm" style={{ background: '#fee2e2', color: 'var(--error)' }}>
                        <FiStopCircle size={13} /> End Class
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Upcoming */}
            <div style={{ marginBottom: 28 }}>
              <div className="td-section-title">📅 Upcoming Classes</div>
              {upcoming.length === 0 ? (
                <div style={{ background: 'white', border: '1px dashed var(--border)', borderRadius: 12, padding: 32, textAlign: 'center', color: 'var(--mid)' }}>
                  No upcoming classes scheduled. Admin will assign classes to you.
                </div>
              ) : upcoming.map(cls => {
                const scheduled = new Date(cls.scheduledAt);
                const now       = new Date();
                const minsAway  = Math.floor((scheduled - now) / 60000);
                const canStart  = minsAway <= 15; // can start 15 min before

                return (
                  <div key={cls._id} className="cls-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{cls.title}</div>
                        {cls.description && <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 6 }}>{cls.description}</div>}
                      </div>
                      <div style={{ background: statusColor.scheduled.bg, color: statusColor.scheduled.color, padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {statusColor.scheduled.label}
                      </div>
                    </div>
                    <div className="cls-meta">
                      <span className="cls-meta-item"><FiBook size={13} /> {cls.subject}</span>
                      <span className="cls-meta-item"><FiClock size={13} />
                       {new Date(cls.scheduledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })} at {new Date(cls.scheduledAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                      </span>
                      <span className="cls-meta-item"><FiClock size={13} /> {cls.duration} min</span>
                      {cls.course && <span className="cls-meta-item"><FiUsers size={13} /> {cls.course.title}</span>}
                    </div>
                    {cls.adminNote && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginTop: 12, fontSize: 13, color: '#92400e' }}>
                        📌 Admin Note: {cls.adminNote}
                      </div>
                    )}
                    <div className="cls-actions">
                      {canStart ? (
                        <button onClick={() => startClass(cls)} className="btn btn-primary">
                          <FiPlay size={14} /> Start Live Class 🔴
                        </button>
                      ) : (
                        <div style={{ fontSize: 13, color: 'var(--mid)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FiClock size={13} />
                          {minsAway > 60
                            ? `Starts in ${Math.floor(minsAway / 60)}h ${minsAway % 60}m`
                            : `Starts in ${minsAway} minutes`
                          }
                          {' '} — Start button appears 15 min before class
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Past classes */}
            {past.length > 0 && (
              <div>
                <div className="td-section-title">✅ Past Classes</div>
                {past.map(cls => (
                  <div key={cls._id} className="cls-card" style={{ opacity: 0.8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{cls.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--mid)' }}>
                          {new Date(cls.scheduledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {' · '}{cls.subject}
                          {cls.course && ' · ' + cls.course.title}
                        </div>
                      </div>
                      <div style={{ background: statusColor[cls.status]?.bg, color: statusColor[cls.status]?.color, padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {statusColor[cls.status]?.label}
                      </div>
                    </div>
                    <div className="cls-actions">
                      {cls.replayUrl ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--success)' }}>
                          <FiLink size={13} />
                          <span>Replay added ✅</span>
                          <a href={cls.replayUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>View</a>
                        </div>
                      ) : cls.status === 'ended' ? (
                        <button onClick={() => { setReplayModal(cls._id); setReplayUrl(''); }} className="btn btn-outline btn-sm">
                          <FiLink size={13} /> Add Replay Link
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {classes.length === 0 && (
              <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--mid)' }}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>📚</div>
                <h3 style={{ marginBottom: 8, color: 'var(--dark)' }}>No Classes Yet</h3>
                <p>Admin will assign classes to you. Check back soon!</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}